import * as Sentry from "@sentry/react";
import { useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { facilitiesMapAtom } from "atoms/map/payload/facilities";
import { wellsMapAtom } from "atoms/map/payload/wells";
import axios, { CancelTokenSource } from "axios";
import { EMPTY_FILTER_ID, SERIES_LIMIT_EXCEEDED } from "constants/app.constants";
import { useAtomValue, useSetAtom } from "jotai";
import { setIsFiltering } from "store/features";
import type { IMapRequest } from "stores/map";
import { useMapStore } from "stores/map";

import { EMPTY_MAP_RESULT, getTxnId as fetchMapData } from "api/map";

export function useFacilityMapUpdater() {
  const dispatch = useDispatch();
  const setFacilityHasToConfirmSize = useMapStore((s) => s.setFacilityHasToConfirmSize);
  const setTxnId = useMapStore((s) => s.setTxnId);

  const wellsLegends = useAtomValue(wellsMapAtom);
  const setFacilitiesMap = useSetAtom(facilitiesMapAtom);

  // Store the current cancel token source for request cancellation
  const cancelTokenSourceRef = useRef<Maybe<CancelTokenSource>>(null);

  /**
   * Fetches facility map data and updates atoms/Redux state
   * @param request - The facility map request
   * @param screenshot - Whether this is for screenshot mode (skips atom updates)
   */
  const fetchFacilityMap = async (
    request: IMapRequest,
    screenshot = false,
    { skipTxnIdDispatch = false } = {}
  ) => {
    // Cancel any pending request before starting a new one
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel();
    }

    // Check if facility filter is empty or invalid - return EMPTY_MAP_RESULT if so
    if (!request?.FilterId || request.FilterId === EMPTY_FILTER_ID) {
      return EMPTY_MAP_RESULT;
    }

    // Create new cancel token for this request
    // eslint-disable-next-line import/no-named-as-default-member
    const cancelTokenSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelTokenSource;

    try {
      dispatch(setIsFiltering(true));
      const result = await fetchMapData(request, cancelTokenSource.token);
      dispatch(setIsFiltering(false));

      // Clear the cancel token ref after successful completion
      cancelTokenSourceRef.current = null;

      // getTxnId returns null on a non-cancel axios failure (5xx, network drop,
      // gateway timeout) instead of throwing -- without this guard, `result.error`
      // below throws a TypeError that the outer catch swallows into an empty map
      // with no toast and no Sentry event.
      if (!result) {
        setFacilityHasToConfirmSize(false);
        Sentry.withScope((scope) => {
          scope.setContext("fetchFacilityMap", { filterId: request.FilterId });
          scope.setLevel("error");
          Sentry.captureException(new Error("fetchFacilityMap: getTxnId returned null"));
        });
        toast.error("Couldn't load the map", { toastId: "fetch-facility-map-error" });
        return EMPTY_MAP_RESULT;
      }

      // Handle series limit exceeded error. Only re-arms the confirm dialog on a
      // first (non-override) request: once OverrideSizeLimit is set, eva-data-svc-rs's
      // ceiling is bypassed entirely (EVA-33), so this response is no longer the
      // expected confirm-and-retry case -- it means the retry hit the ceiling anyway
      // (e.g. a deploy-order or header mismatch between the confirmed services). That
      // must not fall through silently: FacilityMapSeriesSizeConfirmation.onConfirm
      // hides the dialog unconditionally right after this call resolves, so treating
      // it as the normal case here would close the dialog with no error shown at all.
      if (result.error === SERIES_LIMIT_EXCEEDED && !request.OverrideSizeLimit) {
        setFacilityHasToConfirmSize(true);
        // result is guaranteed non-null by the `if (!result)` guard above.
        return result;
      } else {
        setFacilityHasToConfirmSize(false);
        // A non-empty, non-SERIES_LIMIT_EXCEEDED error is map-svc's generic catch
        // ("An error occurred - " + e.Message), which can include raw
        // RpcException/Status text (internal hostnames, ports) -- that must not
        // reach the user as-is, so it's reported to Sentry (same shape as
        // api/map.ts's fetchGroupBounds) with a generic toast instead. toastId
        // collapses repeats to one toast: fetchFacilityMap re-runs on every
        // filter/settings change.
        if (result.error) {
          Sentry.withScope((scope) => {
            scope.setContext("fetchFacilityMap", { filterId: request.FilterId });
            scope.setLevel("error");
            Sentry.captureException(new Error(result.error));
          });
          toast.error("Couldn't load the map", { toastId: "fetch-facility-map-error" });
        }
      }

      // Update atoms and Redux state (skip for screenshot mode or series limit exceeded)
      if (!screenshot) {
        const dispatchPayload = {
          ...wellsLegends,
          bBox: result?.bBox ?? { sw: [], ne: [] },
          facilityLegend: result?.legend ?? {
            legendItems: [],
            title: ""
          },
          facilityId: result?.id ?? ""
        };

        if (!skipTxnIdDispatch) {
          setTxnId(dispatchPayload);
        }

        setFacilitiesMap({
          id: result?.id ?? "",
          legend: result?.legend ?? { legendItems: [], title: "" }
        });
      }

      return result;
    } catch (err) {
      // Only clear the filtering state for real errors, not cancellations.
      // When a newer request cancels this one, it is already running and will
      // clear isFiltering when it completes — stopping the spinner here would
      // cause a visible flicker before the newer request finishes.
      if (!axios.isCancel(err)) {
        dispatch(setIsFiltering(false));
      }

      // Check if this error is due to cancellation
      if (axios.isCancel(err)) {
        return EMPTY_MAP_RESULT;
      }

      return EMPTY_MAP_RESULT;
    }
  };

  const cancelFacilityRequest = () => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel();
      cancelTokenSourceRef.current = null;
    }
  };

  return {
    fetchFacilityMap,
    cancelFacilityRequest
  };
}
